import { useCallback, useEffect, useRef, useState } from 'react';

const defaultShouldDebounce = (value) => value !== '';

const useSearchInputState = ({
  initialValue = '',
  debounceMs = 250,
  shouldDebounce = defaultShouldDebounce
} = {}) => {
  const [inputValue, setInputValueState] = useState(initialValue);
  const [queryValue, setQueryValueState] = useState(initialValue);
  const debounceRef = useRef(null);
  const isComposingRef = useRef(false);

  const clearDebounce = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  const commitQueryValue = useCallback((nextValue) => {
    clearDebounce();
    setQueryValueState(nextValue);
  }, [clearDebounce]);

  const setInputValue = useCallback((nextValue) => {
    setInputValueState(nextValue);
    clearDebounce();

    if (isComposingRef.current) {
      return;
    }

    if (!shouldDebounce(nextValue)) {
      setQueryValueState(nextValue);
      return;
    }

    if (nextValue === queryValue) {
      return;
    }

    debounceRef.current = setTimeout(() => {
      setQueryValueState(nextValue);
    }, debounceMs);
  }, [clearDebounce, debounceMs, queryValue, shouldDebounce]);

  const setQueryValue = useCallback((nextValue) => {
    clearDebounce();
    setInputValueState(nextValue);
    setQueryValueState(nextValue);
  }, [clearDebounce]);

  const onCompositionStart = useCallback(() => {
    isComposingRef.current = true;
    clearDebounce();
  }, [clearDebounce]);

  const onCompositionEnd = useCallback((event) => {
    const nextValue = event?.currentTarget?.value ?? '';
    isComposingRef.current = false;
    setInputValueState(nextValue);
    commitQueryValue(nextValue);
  }, [commitQueryValue]);

  useEffect(() => {
    return () => clearDebounce();
  }, [clearDebounce]);

  return {
    inputValue,
    setInputValue,
    queryValue,
    setQueryValue,
    onCompositionStart,
    onCompositionEnd
  };
};

export default useSearchInputState;
